import React, { useCallback, useMemo } from "react";
import { DateTime } from "luxon";
import { Flex, AgGridReact, DatePicker } from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import { datePickerFilterWrapper, filterText } from "../styles_vanilla.css";
import { DateRangeFilterModel } from "./types";

/**
 * A reusable date range filter for AG Grid columns.
 * Renders two DatePickers (start date / end date) and emits
 * a `DateRangeFilterModel` via `onModelChange`.
 *
 * Local state is derived from `filterParams.model` so that external
 * resets (e.g. AG Grid filter restore) are reflected automatically.
 */
export const DateRangeFilter = (filterParams: AgGridReact.CustomFilterProps) => {
  const { onModelChange } = filterParams;
  const currentModel = filterParams.model as DateRangeFilterModel | null;
  const startDate = currentModel?.startDate ?? null;
  const endDate = currentModel?.endDate ?? null;

  const handleStartChange = useCallback(
    (date: Date | null) => {
      const isoDate = date ? DateTime.fromJSDate(date).toISODate() ?? null : null;
      if (!isoDate && !endDate) {
        onModelChange(null);
      } else {
        onModelChange({ startDate: isoDate, endDate });
      }
    },
    [onModelChange, endDate],
  );

  const handleEndChange = useCallback(
    (date: Date | null) => {
      const isoDate = date ? DateTime.fromJSDate(date).toISODate() ?? null : null;
      if (!startDate && !isoDate) {
        onModelChange(null);
      } else {
        onModelChange({ startDate, endDate: isoDate });
      }
    },
    [onModelChange, startDate],
  );

  const selectedStartDate = useMemo(() => {
    if (!startDate) return null;
    return DateTime.fromISO(startDate).toJSDate();
  }, [startDate]);

  const selectedEndDate = useMemo(() => {
    if (!endDate) return null;
    return DateTime.fromISO(endDate).toJSDate();
  }, [endDate]);

  // Server-side filtering — doesFilterPass always returns true
  AgGridReact.useGridFilter({ doesFilterPass: () => true });

  return (
    <Flex flexDirection="column" gap={Sizes.XS}>
      <DatePicker
        className={filterText}
        wrapperClassName={datePickerFilterWrapper}
        isClearable
        selected={selectedStartDate}
        onChange={handleStartChange}
        selectsStart
        startDate={selectedStartDate}
        endDate={selectedEndDate}
        maxDate={selectedEndDate ?? undefined}
        dateFormat="MMM d, yyyy"
        placeholderText="Start date"
      />
      <DatePicker
        className={filterText}
        wrapperClassName={datePickerFilterWrapper}
        isClearable
        selected={selectedEndDate}
        onChange={handleEndChange}
        selectsEnd
        startDate={selectedStartDate}
        endDate={selectedEndDate}
        minDate={selectedStartDate ?? undefined}
        dateFormat="MMM d, yyyy"
        placeholderText="End date"
      />
    </Flex>
  );
};
